<template>
  <a-drawer
    :width="1280"
    :open="open"
    class="resident-report-drawer"
    :body-style="{ paddingBottom: '32px' }"
    :footer-style="{ padding: '20px 40px 40px 40px' }"
    :header-style="{ padding: '32px 40px' }"
    :closable="true"
    @close="requestCloseDrawer"
  >
    <template #title>
      <span class="resident-report-drawer__title">{{ drawerTitle }}</span>
    </template>

    <template #closeIcon>
      <span class="resident-report-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <a-form layout="vertical" class="resident-report-drawer__form">
      <section class="resident-report-drawer__section resident-report-drawer__section--common">
        <h3 class="resident-report-drawer__section-title">1. Общее</h3>
        <div class="resident-report-drawer__section-content">
          <a-form-item class="stc-ant-field-skin" label="Наименование организации" required>
            <a-input
              v-model:value="form.companyName"
              placeholder="ООО «Компания_название»"
              disabled
            />
          </a-form-item>

          <a-form-item class="stc-ant-field-skin" label="Отчётный период" required>
            <a-select v-model:value="form.reportYear" :options="yearOptions" />
          </a-form-item>
        </div>
      </section>

      <section class="resident-report-drawer__section">
        <h3 class="resident-report-drawer__section-title">2. Показатели*</h3>
        <p class="resident-report-drawer__section-note">*За выбранный отчётный период.</p>
        <div class="resident-report-drawer__section-content">
          <a-table
            class="resident-report-drawer__metrics-table"
            :columns="metricColumns"
            :data-source="metrics"
            :pagination="false"
            :bordered="true"
            row-key="key"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'label'">
                {{ record.label }}
              </template>

              <template v-else-if="column.key === 'plan'">
                <span
                  class="resident-report-drawer__metric-cell"
                  contenteditable="true"
                  inputmode="decimal"
                  role="textbox"
                  :aria-label="`${record.label}: план`"
                  @blur="syncMetricCellText(record, 'plan', $event)"
                  @keydown.enter.prevent="blurEditableCell"
                >
                  {{ formatMetricValue(record.plan, record.precision) }}
                </span>
              </template>

              <template v-else-if="column.key === 'fact'">
                <span
                  class="resident-report-drawer__metric-cell"
                  contenteditable="true"
                  inputmode="decimal"
                  role="textbox"
                  :aria-label="`${record.label}: факт`"
                  @blur="syncMetricCellText(record, 'fact', $event)"
                  @keydown.enter.prevent="blurEditableCell"
                >
                  {{ formatMetricValue(record.fact, record.precision) }}
                </span>
              </template>

              <template v-else-if="column.key === 'deviation'">
                <span class="resident-report-drawer__deviation">
                  {{ formatDeviation(record) }}
                </span>
              </template>
            </template>
          </a-table>
        </div>
      </section>

      <section class="resident-report-drawer__section">
        <h3 class="resident-report-drawer__section-title">3. Детали</h3>
        <div class="resident-report-drawer__details-grid">
          <a-form-item
            v-for="field in detailFields"
            :key="field.name"
            class="stc-ant-field-skin resident-report-drawer__textarea-field"
            :label="field.label"
            :required="field.required"
          >
            <a-textarea
              v-model:value="form[field.name]"
              :maxlength="TEXTAREA_MAX_LENGTH"
              placeholder="Текст"
              :auto-size="{ minRows: 6, maxRows: 6 }"
              show-count
            />
          </a-form-item>
        </div>
      </section>

      <section class="resident-report-drawer__section">
        <h3 class="resident-report-drawer__section-title">4. Документы</h3>
        <div class="resident-report-drawer__documents">
          <FormFileUpload ref="documentsUploadRef" v-model="documentItems" />
        </div>
      </section>
    </a-form>

    <template #footer>
      <div class="resident-report-drawer__footer">
        <div class="resident-report-drawer__footer-start"></div>
        <div class="resident-report-drawer__footer-actions">
          <button type="button" class="btn btn-secondary btn-medium" @click="requestCloseDrawer">
            Отмена
          </button>
          <button
            type="button"
            class="btn btn-secondary btn-medium"
            :disabled="isActionDisabled"
            @click="saveDraftReport"
          >
            {{ isSubmitting ? 'Сохранение...' : 'Сохранить черновик' }}
          </button>
          <button
            type="button"
            class="btn btn-primary btn-medium"
            :disabled="isActionDisabled"
            @click="openSignCertificateDrawer"
          >
            {{ isSubmitting ? 'Проверка...' : 'Подписать и отправить' }}
          </button>
        </div>
      </div>
    </template>
  </a-drawer>

  <a-modal
    :open="isCloseConfirmOpen"
    class="resident-report-drawer-confirm"
    :footer="null"
    :closable="false"
    :mask-closable="false"
    centered
    width="480px"
  >
    <h3 class="resident-report-drawer-confirm__title">{{ closeConfirmTitle }}</h3>
    <p class="resident-report-drawer-confirm__description">
      Все загруженные файлы и внесенные изменения <br />
      будут утеряны. Вы уверены?
    </p>
    <div class="resident-report-drawer-confirm__divider" aria-hidden="true" />
    <div class="resident-report-drawer-confirm__actions">
      <button type="button" class="btn btn-secondary btn-medium" @click="keepDrawerOpen">
        Нет
      </button>
      <button type="button" class="btn btn-error btn-medium" @click="confirmCloseDrawer">
        Да, отменить
      </button>
    </div>
  </a-modal>

  <a-modal
    :open="isDeleteConfirmOpen"
    class="resident-report-drawer-confirm"
    :footer="null"
    :closable="false"
    :mask-closable="false"
    centered
    width="480px"
  >
    <h3 class="resident-report-drawer-confirm__title">Удалить отчет?</h3>
    <p class="resident-report-drawer-confirm__description">
      Все загруженные файлы и внесенные параметры <br />
      будут удалены. Вы уверены?
    </p>
    <div class="resident-report-drawer-confirm__divider" aria-hidden="true" />
    <div class="resident-report-drawer-confirm__actions">
      <button
        type="button"
        class="btn btn-secondary btn-medium"
        :disabled="isDeleting"
        @click="cancelDeleteConfirm"
      >
        Нет
      </button>
      <button
        type="button"
        class="btn btn-error btn-medium"
        :disabled="isDeleting"
        @click="confirmDeleteReport"
      >
        {{ isDeleting ? 'Удаление...' : 'Да, удалить' }}
      </button>
    </div>
  </a-modal>

  <a-modal
    :open="isNoCertificatesModalOpen"
    class="resident-report-no-certs-modal"
    :footer="null"
    :closable="false"
    :mask-closable="false"
    centered
    width="480px"
  >
    <h3 class="resident-report-no-certs-modal__title">Нет сертификатов</h3>
    <p class="resident-report-no-certs-modal__description">
      Убедитесь, что КриптоПро CSP и браузерный плагин установлены, а носитель ключа
      (токен/смарт-карта) подключён к компьютеру.
    </p>
    <div class="resident-report-no-certs-modal__divider" aria-hidden="true" />
    <div class="resident-report-no-certs-modal__actions">
      <button type="button" class="btn btn-soft" @click="closeNoCertificatesModal">Закрыть</button>
      <a
        class="btn btn-primary"
        href="https://docs.cryptopro.ru/cades/plugin"
        target="_blank"
        rel="noopener noreferrer"
      >
        Инструкция по настройке
      </a>
    </div>
  </a-modal>

  <a-drawer
    :width="1040"
    :open="isCertificateDrawerOpen"
    class="resident-report-sign-drawer"
    :closable="true"
    :body-style="{ paddingBottom: '96px' }"
    @close="closeCertificateDrawer"
  >
    <template #title>
      <span class="resident-report-sign-drawer__title">Подписать отчет</span>
    </template>

    <template #closeIcon>
      <span class="resident-report-sign-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <div class="resident-report-sign-drawer__summary">
      <div class="resident-report-sign-drawer__summary-item">
        <span class="resident-report-sign-drawer__summary-label">Название отчета</span>
        <span class="resident-report-sign-drawer__summary-value">{{ signReportName }}</span>
      </div>
      <div class="resident-report-sign-drawer__summary-item">
        <span class="resident-report-sign-drawer__summary-label">Отчетный период</span>
        <span class="resident-report-sign-drawer__summary-value">{{ form.reportYear }} г.</span>
      </div>
      <div class="resident-report-sign-drawer__summary-item">
        <span class="resident-report-sign-drawer__summary-label">Дата создания</span>
        <span class="resident-report-sign-drawer__summary-value">{{ signCreatedAt }}</span>
      </div>
    </div>

    <section class="resident-report-sign-drawer__certificates">
      <h3 class="resident-report-sign-drawer__section-title">Сертификаты</h3>
      <p class="resident-report-sign-drawer__section-description">
        Выберите действующий сертификат для подписания.
      </p>

      <a-table
        class="resident-report-sign-drawer__certificates-table"
        :columns="certificateColumns"
        :data-source="certificates"
        :pagination="false"
        :bordered="true"
        :loading="isCertificatesLoading"
        :scroll="{ y: 500, x: 740 }"
        row-key="thumbprint"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'select'">
            <a-radio
              :checked="selectedCertificateThumbprint === record.thumbprint"
              @change="selectCertificate(record.thumbprint)"
            />
          </template>

          <template v-else-if="column.key === 'name'">
            {{ getCertificateOwner(record) }}
          </template>

          <template v-else-if="column.key === 'owner'">
            {{ getCertificateOwner(record) }}
          </template>

          <template v-else-if="column.key === 'organization'">
            {{ getCertificateOrganization(record) || '-' }}
          </template>

          <template v-else-if="column.key === 'serialNumber'">
            {{ record.serialNumber }}
          </template>

          <template v-else-if="column.key === 'validTo'">
            <span
              class="resident-report-sign-drawer__cert-validity"
              :class="{ 'is-expired': isCertificateExpired(record) }"
            >
              <span class="resident-report-sign-drawer__cert-validity-dot" aria-hidden="true" />
              {{ formatCertificateDate(record.validTo) }}
            </span>
          </template>
        </template>
      </a-table>
    </section>

    <template #footer>
      <div class="resident-report-sign-drawer__footer">
        <button type="button" class="btn btn-soft" @click="closeCertificateDrawer">Отмена</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!selectedCertificateThumbprint || isSigning"
          @click="signSelectedCertificate"
        >
          {{ isSigning ? 'Подписание...' : 'Подписать' }}
        </button>
      </div>
    </template>
  </a-drawer>

  <a-modal
    :open="isSignSuccessModalOpen"
    class="resident-report-sign-success-modal"
    :footer="null"
    :closable="false"
    :mask-closable="false"
    centered
    width="420px"
  >
    <div class="resident-report-sign-success-modal__icon" aria-hidden="true">
      <img :src="SUCCESS_ICON" alt="" />
    </div>
    <h3 class="resident-report-sign-success-modal__title">Отчет подписан и отправлен</h3>
    <dl class="resident-report-sign-success-modal__details">
      <div class="resident-report-sign-success-modal__details-row">
        <dt>Подписант</dt>
        <dd>{{ signedReportInfo.signer }}</dd>
      </div>
      <div class="resident-report-sign-success-modal__details-row">
        <dt>Сертификат, №</dt>
        <dd>{{ signedReportInfo.serialNumber }}</dd>
      </div>
      <div class="resident-report-sign-success-modal__details-row">
        <dt>Дата подписания</dt>
        <dd>{{ signedReportInfo.signedAt }}</dd>
      </div>
    </dl>

    <div class="continue-button-container">
      <button type="button" class="btn btn-outline btn-medium" @click="closeSignSuccessModal">
        Продолжить
      </button>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import type { TableColumnsType } from 'ant-design-vue';
import FormFileUpload, { type FileUploadItem } from '@/js/components/form/form-file-upload.vue';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
  showStcWarningNotification,
} from '@/js/components/stc-notification';
import { getAssetsPath } from '@/js/utils/getAssetsDir';
import {
  fetchPdfAsBase64,
  deleteReport,
  saveReport,
  signReport,
} from '../resident-report/report-api';
import {
  getReportDetailActions,
  getStatusModifier,
} from '../resident-report-detail/map-report-detail';
import { navigateToReportList } from '../resident-report-detail/get-report-id-from-url';
import { CryptoProGateway } from '../sign-widget/cryptopro-gateway';
import type { CertInfo, SignProfile } from '../sign-widget/sign-types';
import type { ReportEditPayload } from './report-edit-payload';

const SIGN_PROFILE: SignProfile = 'CAdES-BES';
const SUCCESS_ICON = getAssetsPath('/assets/images/icons/success-status.svg');

type DetailFieldName =
  | 'comment'
  | 'projectNames'
  | 'projectReadiness'
  | 'rids'
  | 'commercialization'
  | 'stateSupport';

type MetricItem = {
  key: string;
  label: string;
  plan: number | null;
  fact: number | null;
  precision: number;
};
type MetricValueKey = 'plan' | 'fact';
type ReportDrawerMode = 'create' | 'edit';
type ReportFormState = {
  companyName: string;
  reportYear: string;
  details: Record<DetailFieldName, string>;
  metrics: Array<Pick<MetricItem, 'plan' | 'fact'>>;
  documentsCount: number;
};
type SignedReportInfo = {
  signer: string;
  serialNumber: string;
  signedAt: string;
};
type PreparedSignPayload = {
  id: number;
  content: string;
};

const TEXTAREA_MAX_LENGTH = 512;
const MOCK_CERTIFICATES_QUERY_PARAM = 'mockReportCertificates';
const MOCK_CERTIFICATES_STORAGE_KEY = 'stc:mock-report-certificates';

const open = ref(false);
const mode = ref<ReportDrawerMode>('create');
const reportId = ref<number | null>(null);
const reportStatus = ref<string | null>(null);
const isCloseConfirmOpen = ref(false);
const isDeleteConfirmOpen = ref(false);
const isNoCertificatesModalOpen = ref(false);
const isCertificateDrawerOpen = ref(false);
const isSignSuccessModalOpen = ref(false);
const isCertificatesLoading = ref(false);
const isSigning = ref(false);
const isSubmitting = ref(false);
const hasSubmitError = ref(false);
const isDeleting = ref(false);
const documentsUploadRef = ref<InstanceType<typeof FormFileUpload> | null>(null);
const documentItems = ref<FileUploadItem[]>([]);
const initialFormState = ref<ReportFormState | null>(null);
const certificates = ref<CertInfo[]>([]);
const selectedCertificateThumbprint = ref<string | null>(null);
const preparedSignPayload = ref<PreparedSignPayload | null>(null);
const signCreatedAt = ref(formatShortDate(new Date()));
const cryptoProGateway = new CryptoProGateway();
const signedReportInfo = reactive<SignedReportInfo>({
  signer: '-',
  serialNumber: '-',
  signedAt: '-',
});

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 3 }, (_, index) => {
  const year = currentYear - 1 + index;
  return { label: `${year} г.`, value: String(year) };
});

const form = reactive<
  Record<DetailFieldName, string> & { companyName: string; reportYear: string }
>({
  companyName: '',
  reportYear: String(currentYear),
  comment: '',
  projectNames: '',
  projectReadiness: '',
  rids: '',
  commercialization: '',
  stateSupport: '',
});

const metrics = reactive<MetricItem[]>([
  { key: 'revenue', label: 'Выручка, руб', plan: null, fact: null, precision: 0 },
  {
    key: 'invest_infra',
    label: 'Инвестиции в инфраструктуру ИНТЦ МГУ, руб',
    plan: null,
    fact: null,
    precision: 0,
  },
  { key: 'invest_rnd', label: 'Инвестиции в НИОКР, руб', plan: null, fact: null, precision: 0 },
  { key: 'export_share', label: 'Доля экспорта, %', plan: null, fact: null, precision: 2 },
  {
    key: 'employees',
    label: 'Численность работников организации, чел.',
    plan: null,
    fact: null,
    precision: 0,
  },
  {
    key: 'rid',
    label: 'Кол-во результатов интеллектуальной деятельности, шт',
    plan: null,
    fact: null,
    precision: 0,
  },
]);

const metricColumns: TableColumnsType<MetricItem> = [
  { title: 'Показатель', key: 'label', dataIndex: 'label' },
  { title: 'План', key: 'plan', width: 140 },
  { title: 'Факт', key: 'fact', width: 140 },
  { title: 'Отклонение, %', key: 'deviation', width: 140 },
];

const certificateColumns: TableColumnsType<CertInfo> = [
  { title: '', key: 'select', width: 40, fixed: 'left' },
  { title: 'Название', key: 'name', width: 170 },
  { title: 'Владелец', key: 'owner', width: 145 },
  { title: 'Организация', key: 'organization', width: 230 },
  { title: 'Номер', key: 'serialNumber', width: 120 },
  { title: 'Срок действия', key: 'validTo', width: 120 },
];

const detailFields: Array<{ name: DetailFieldName; label: string; required: boolean }> = [
  { name: 'comment', label: 'Общий комментарий', required: false },
  { name: 'projectNames', label: 'Наименование проекта', required: true },
  {
    name: 'projectReadiness',
    label: 'Стадия готовности реализованного НТП (УГТ), по ГОСТ-Р 71726-2024',
    required: true,
  },
  { name: 'rids', label: 'Перечень РИД, возникших при реализации НТП', required: true },
  {
    name: 'commercialization',
    label: 'Сведения о вовлечении результатов НТД в гражданский оборот',
    required: true,
  },
  {
    name: 'stateSupport',
    label: 'Сведения об использовании государственных мер поддержки',
    required: true,
  },
];

const areRequiredFieldsFilled = computed(
  () =>
    form.companyName.trim().length > 0 &&
    form.reportYear.trim().length > 0 &&
    metrics.every((metric) => metric.plan !== null && metric.fact !== null) &&
    detailFields
      .filter((field) => field.required)
      .every((field) => form[field.name].trim().length > 0),
);

const isActionDisabled = computed(
  () => isSubmitting.value || hasSubmitError.value || !areRequiredFieldsFilled.value,
);
const drawerTitle = computed(() =>
  mode.value === 'edit' ? 'Редактирование отчёта' : 'Новый отчёт',
);
const canDeleteReport = computed(
  () =>
    mode.value === 'edit' &&
    reportId.value !== null &&
    getReportDetailActions(getStatusModifier(reportStatus.value)).canDelete,
);
const signReportName = computed(() =>
  form.companyName.trim() ? `Отчет_${form.companyName.trim()}_${form.reportYear}` : 'Отчет',
);
const closeConfirmTitle = computed(() =>
  mode.value === 'edit' ? 'Отменить редактирование отчета?' : 'Отменить создание отчета?',
);
const hasUnsavedChanges = computed(() => {
  const initialState = initialFormState.value;
  if (!initialState) return false;

  return !isSameReportState(captureReportState(), initialState);
});

function getDefaultCompanyName(): string {
  const sidebarName = document.querySelector<HTMLElement>('[data-resident-name]');
  const sidebarValue = sidebarName?.textContent?.trim();
  if (sidebarValue) {
    return sidebarValue;
  }

  const input = document.querySelector<HTMLInputElement>(
    '#personalDataForm input[name="company_name"]',
  );
  return input?.value?.trim() ?? '';
}

function normalizeReportYear(value: string): string {
  return value.replace(/\s*г\.?\s*$/i, '').trim();
}

function createExistingDocumentItem(
  document: NonNullable<ReportEditPayload['documents']>[number],
): FileUploadItem {
  const fileName = document.name.replace(/^Файл\.\s*/i, '').trim() || document.name;
  const fileSize = document.size ?? 0;
  const file = new File([new ArrayBuffer(fileSize)], fileName, {
    type: 'application/octet-stream',
  });

  return {
    id: `existing-${document.url}-${fileName}`,
    file,
    status: 'ready',
    progress: 100,
  };
}

function applyEditPayload(payload: ReportEditPayload): void {
  reportId.value = payload.id ?? null;
  reportStatus.value = payload.status ?? null;
  form.companyName = payload.companyName;
  form.reportYear = normalizeReportYear(payload.reportYear);
  form.comment = payload.comment ?? '';
  form.projectNames = payload.projectNames ?? '';
  form.projectReadiness = payload.projectReadiness ?? '';
  form.rids = payload.rids ?? '';
  form.commercialization = payload.commercialization ?? '';
  form.stateSupport = payload.stateSupport ?? '';

  if (payload.metrics?.length) {
    payload.metrics.forEach((sourceMetric, index) => {
      const metric =
        (sourceMetric.key ? metrics.find((item) => item.key === sourceMetric.key) : null) ??
        metrics[index];
      if (!metric) return;

      metric.plan = sourceMetric.plan;
      metric.fact = sourceMetric.fact;
    });
  }

  documentItems.value = (payload.documents ?? []).map(createExistingDocumentItem);
}

function showDrawer(nextMode: ReportDrawerMode = 'create', payload?: ReportEditPayload): void {
  if (nextMode === 'create') {
    reportId.value = null;
    reportStatus.value = null;
    restoreReportState(getEmptyReportState());
    const companyName = getDefaultCompanyName();
    if (companyName) {
      form.companyName = companyName;
    }
  } else if (payload) {
    applyEditPayload(payload);
  }

  mode.value = nextMode;
  isCloseConfirmOpen.value = false;
  isDeleteConfirmOpen.value = false;
  hasSubmitError.value = false;
  initialFormState.value = captureReportState();
  open.value = true;
}

function closeDrawer(options: { reset?: boolean } = {}): void {
  if (options.reset) {
    resetReportForm();
    reportId.value = null;
    reportStatus.value = null;
    hasSubmitError.value = false;
  }
  isCloseConfirmOpen.value = false;
  isDeleteConfirmOpen.value = false;
  open.value = false;
}

function openDeleteConfirm(): void {
  if (!reportId.value || !canDeleteReport.value) return;
  isDeleteConfirmOpen.value = true;
}

function showDeleteConfirm(id: number): void {
  reportId.value = id;
  isDeleteConfirmOpen.value = true;
}

function cancelDeleteConfirm(): void {
  isDeleteConfirmOpen.value = false;
}

async function confirmDeleteReport(): Promise<void> {
  if (!reportId.value || isDeleting.value) return;

  isDeleting.value = true;

  try {
    const successMessage = await deleteReport(reportId.value);
    showStcSuccessNotification(successMessage || 'Отчёт успешно удалён');
    window.dispatchEvent(new CustomEvent('residentReports:changed'));
    isDeleteConfirmOpen.value = false;

    if (open.value) {
      closeDrawer({ reset: true });
    }

    if (document.querySelector('.resident-report-detail')) {
      navigateToReportList();
    }
  } catch (error) {
    showStcErrorNotification(error instanceof Error ? error.message : 'Не удалось удалить отчёт');
  } finally {
    isDeleting.value = false;
  }
}

function requestCloseDrawer(): void {
  if (!hasUnsavedChanges.value) {
    closeDrawer();
    return;
  }

  isCloseConfirmOpen.value = true;
}

function keepDrawerOpen(): void {
  isCloseConfirmOpen.value = false;
}

function confirmCloseDrawer(): void {
  closeDrawer({ reset: true });
}

function resetReportForm(): void {
  restoreReportState(initialFormState.value ?? getEmptyReportState());
}

function getEmptyReportState(): ReportFormState {
  return {
    companyName: '',
    reportYear: String(currentYear),
    details: detailFields.reduce(
      (acc, field) => {
        acc[field.name] = '';
        return acc;
      },
      {} as Record<DetailFieldName, string>,
    ),
    metrics: metrics.map(() => ({ plan: null, fact: null })),
    documentsCount: 0,
  };
}

function captureReportState(): ReportFormState {
  return {
    companyName: form.companyName,
    reportYear: form.reportYear,
    details: detailFields.reduce(
      (acc, field) => {
        acc[field.name] = form[field.name];
        return acc;
      },
      {} as Record<DetailFieldName, string>,
    ),
    metrics: metrics.map((metric) => ({ plan: metric.plan, fact: metric.fact })),
    documentsCount: documentItems.value.length,
  };
}

function restoreReportState(state: ReportFormState): void {
  form.companyName = state.companyName;
  form.reportYear = state.reportYear;
  detailFields.forEach((field) => {
    form[field.name] = state.details[field.name];
  });
  metrics.forEach((metric, index) => {
    const initialMetric = state.metrics[index];
    metric.plan = initialMetric?.plan ?? null;
    metric.fact = initialMetric?.fact ?? null;
  });
  documentsUploadRef.value?.reset();
  documentItems.value = [];
}

function isSameReportState(currentState: ReportFormState, initialState: ReportFormState): boolean {
  if (
    currentState.companyName !== initialState.companyName ||
    currentState.reportYear !== initialState.reportYear ||
    currentState.documentsCount !== initialState.documentsCount
  ) {
    return false;
  }

  const detailsChanged = detailFields.some(
    (field) => currentState.details[field.name] !== initialState.details[field.name],
  );
  if (detailsChanged) return false;

  return currentState.metrics.every((metric, index) => {
    const initialMetric = initialState.metrics[index];
    return metric.plan === initialMetric?.plan && metric.fact === initialMetric?.fact;
  });
}

function formatDeviation(metric: MetricItem): string {
  const plan = Number(metric.plan ?? 0);
  const fact = Number(metric.fact ?? 0);

  if (!plan) return '0.00';

  return (((fact - plan) / plan) * 100).toFixed(2);
}

function parseMetricValue(value: string, precision: number): number | null {
  const normalizedValue = value.replace(/\s+/g, '').replace(',', '.');
  if (!normalizedValue) return null;

  const parsedValue = Number(normalizedValue);
  if (!Number.isFinite(parsedValue)) return null;

  const positiveValue = Math.max(parsedValue, 0);
  return Number(positiveValue.toFixed(precision));
}

function formatMetricValue(value: number | null, precision: number): string {
  if (value === null) return '';

  return precision > 0 ? value.toFixed(precision) : String(value);
}

function getEditableCell(event: Event): HTMLElement | null {
  return event.target instanceof HTMLElement ? event.target : null;
}

function syncMetricCellText(metric: MetricItem, key: MetricValueKey, event: Event): void {
  const cell = getEditableCell(event);
  if (!cell) return;

  metric[key] = parseMetricValue(cell.textContent ?? '', metric.precision);
  cell.textContent = formatMetricValue(metric[key], metric.precision);
}

function blurEditableCell(event: Event): void {
  getEditableCell(event)?.blur();
}

async function openSignCertificateDrawer(): Promise<void> {
  if (isSubmitting.value) return;

  if (!areRequiredFieldsFilled.value) {
    showStcWarningNotification('Заполните все обязательные поля');
    return;
  }

  isSubmitting.value = true;
  isCertificatesLoading.value = true;
  signCreatedAt.value = formatShortDate(new Date());
  preparedSignPayload.value = null;

  try {
    const saveResult = await saveReport(buildReportFormData(), reportId.value);
    const savedReportId = saveResult.data?.id ?? reportId.value;
    const pdfUrl = saveResult.data?.pdfUrl;

    if (!savedReportId || !pdfUrl) {
      throw new Error('Сервер не вернул данные для подписи');
    }

    reportId.value = savedReportId;
    preparedSignPayload.value = {
      id: savedReportId,
      content: await fetchPdfAsBase64(pdfUrl),
    };
  } catch (error) {
    hasSubmitError.value = true;
    showStcErrorNotification(
      error instanceof Error ? error.message : 'Не удалось подготовить отчёт к подписанию',
    );
    return;
  } finally {
    isSubmitting.value = false;
  }

  try {
    await cryptoProGateway.checkAvailability();
    const certs = getMockCertificatesForPreview() ?? (await cryptoProGateway.getCertificates());

    if (!certs.length) {
      openNoCertificatesModal();
      return;
    }

    certificates.value = certs;
    selectedCertificateThumbprint.value = certs[0]?.thumbprint ?? null;
    isCertificateDrawerOpen.value = true;
  } catch {
    openNoCertificatesModal();
  } finally {
    isCertificatesLoading.value = false;
  }
}

function openNoCertificatesModal(): void {
  certificates.value = [];
  selectedCertificateThumbprint.value = null;
  isNoCertificatesModalOpen.value = true;
}

function getMockCertificatesForPreview(): CertInfo[] | null {
  if (process.env.NODE_ENV !== 'development') return null;

  const params = new URLSearchParams(window.location.search);
  const isEnabledByQuery = params.has(MOCK_CERTIFICATES_QUERY_PARAM);
  const isEnabledByStorage = localStorage.getItem(MOCK_CERTIFICATES_STORAGE_KEY) === 'true';

  if (!isEnabledByQuery && !isEnabledByStorage) return null;

  return [
    {
      thumbprint: 'MOCK_CERT_001',
      subjectName: 'CN=Иванов И. П., O=ООО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2026-01-01T00:00:00.000Z',
      validTo: '2026-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_002',
      subjectName: 'CN=Петров И. П., O=ОАО «Компания»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '123.456.789.0',
      validFrom: '2026-01-01T00:00:00.000Z',
      validTo: '2026-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_003',
      subjectName: 'CN=Константинов И. П., O=ЗАО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2025-01-01T00:00:00.000Z',
      validTo: '2025-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_003',
      subjectName: 'CN=Константинов И. П., O=ЗАО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2025-01-01T00:00:00.000Z',
      validTo: '2025-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_003',
      subjectName: 'CN=Константинов И. П., O=ЗАО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2025-01-01T00:00:00.000Z',
      validTo: '2025-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_003',
      subjectName: 'CN=Константинов И. П., O=ЗАО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2025-01-01T00:00:00.000Z',
      validTo: '2025-05-03T00:00:00.000Z',
    },
    {
      thumbprint: 'MOCK_CERT_003',
      subjectName: 'CN=Константинов И. П., O=ЗАО «Компания_имя»',
      issuerName: 'CN=Тестовый удостоверяющий центр',
      serialNumber: '098.765.432.1',
      validFrom: '2025-01-01T00:00:00.000Z',
      validTo: '2025-05-03T00:00:00.000Z',
    },
  ];
}

function closeNoCertificatesModal(): void {
  isNoCertificatesModalOpen.value = false;
}

function closeCertificateDrawer(): void {
  isCertificateDrawerOpen.value = false;
  selectedCertificateThumbprint.value = null;
  preparedSignPayload.value = null;
}

function closeSignSuccessModal(): void {
  isSignSuccessModalOpen.value = false;
  window.dispatchEvent(new CustomEvent('residentReports:changed'));
}

function selectCertificate(thumbprint: string): void {
  selectedCertificateThumbprint.value = thumbprint;
}

function getCertificateOwner(cert: CertInfo): string {
  return extractDnValue(cert.subjectName, 'CN') || cert.subjectName;
}

function getCertificateOrganization(cert: CertInfo): string {
  return extractDnValue(cert.subjectName, 'O');
}

function isCertificateExpired(cert: CertInfo): boolean {
  const validToDate = new Date(cert.validTo);
  return Number.isNaN(validToDate.getTime()) || validToDate.getTime() < Date.now();
}

function formatCertificateDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || '-';

  return date.toLocaleDateString('ru-RU');
}

function formatShortDate(date: Date): string {
  return date.toLocaleDateString('ru-RU');
}

function formatSignedAt(date: Date): string {
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  return `${time} / ${formatShortDate(date)}`;
}

function extractDnValue(subjectName: string, key: string): string {
  const parts = subjectName.split(',');
  const found = parts.find((part) => part.trim().startsWith(`${key}=`));
  return found ? found.split('=').slice(1).join('=').trim() : '';
}

async function signSelectedCertificate(): Promise<void> {
  if (!selectedCertificateThumbprint.value || isSigning.value) return;

  const selectedCertificate = certificates.value.find(
    (cert) => cert.thumbprint === selectedCertificateThumbprint.value,
  );
  if (!selectedCertificate) {
    showStcWarningNotification('Выберите сертификат для подписания');
    return;
  }

  if (!preparedSignPayload.value) {
    showStcErrorNotification('Нет данных для подписи. Сначала сохраните отчёт.');
    return;
  }

  isSigning.value = true;

  try {
    await cryptoProGateway.checkAvailability();
    const signature = await cryptoProGateway.signDetached(
      preparedSignPayload.value.content,
      selectedCertificate.thumbprint,
      SIGN_PROFILE,
    );

    await signReport(preparedSignPayload.value.id, {
      content: preparedSignPayload.value.content,
      signature,
    });

    signedReportInfo.signer = getCertificateOwner(selectedCertificate);
    signedReportInfo.serialNumber = selectedCertificate.serialNumber || '-';
    signedReportInfo.signedAt = formatSignedAt(new Date());
    isCertificateDrawerOpen.value = false;
    closeDrawer({ reset: true });
    isSignSuccessModalOpen.value = true;
  } catch (error) {
    showStcErrorNotification(error instanceof Error ? error.message : 'Не удалось подписать отчёт');
  } finally {
    isSigning.value = false;
  }
}

function appendMetric(formData: FormData, metric: MetricItem): void {
  formData.append(`${metric.key}_plan`, metric.plan === null ? '' : String(metric.plan));
  formData.append(`${metric.key}_fact`, metric.fact === null ? '' : String(metric.fact));
  formData.append(`${metric.key}_dev`, formatDeviation(metric));
}

function buildReportFormData(): FormData {
  const body = new FormData();

  if (reportId.value !== null) {
    body.append('Id', String(reportId.value));
  }

  body.append('companyName', form.companyName);
  body.append('reportYear', form.reportYear);
  metrics.forEach((metric) => appendMetric(body, metric));
  detailFields.forEach((field) => body.append(field.name, form[field.name]));
  const readyFiles = documentsUploadRef.value?.getReadyFiles() ?? [];
  readyFiles.forEach((file) => body.append('reportAttachments[]', file));

  return body;
}

async function saveDraftReport(): Promise<void> {
  if (isSubmitting.value) return;

  if (!areRequiredFieldsFilled.value) {
    showStcWarningNotification('Заполните все обязательные поля');
    return;
  }

  isSubmitting.value = true;
  documentsUploadRef.value?.setSubmittingState(true);

  try {
    const result = await saveReport(buildReportFormData(), reportId.value);
    const savedReportId = result.data?.id ?? reportId.value;
    if (savedReportId) {
      reportId.value = savedReportId;
    }

    showStcSuccessNotification(
      mode.value === 'edit' ? 'Отчет успешно сохранен.' : 'Отчет успешно создан.',
    );
    documentsUploadRef.value?.reset();
    window.dispatchEvent(new CustomEvent('residentReports:changed'));
    closeDrawer({ reset: true });
  } catch {
    hasSubmitError.value = true;
    showStcErrorNotification(
      mode.value === 'edit' ? 'Не удалось сохранить отчет.' : 'Не удалось создать отчет.',
    );
    documentsUploadRef.value?.resetSubmittingState();
  } finally {
    isSubmitting.value = false;
  }
}

async function submitReport(): Promise<void> {
  if (isSubmitting.value) return;

  if (!areRequiredFieldsFilled.value) {
    showStcWarningNotification('Заполните все обязательные поля');
    return;
  }

  isSubmitting.value = true;
  documentsUploadRef.value?.setSubmittingState(true);

  try {
    const result = await saveReport(buildReportFormData(), reportId.value);
    const savedReportId = result.data?.id ?? reportId.value;
    if (savedReportId) {
      reportId.value = savedReportId;
    }

    showStcSuccessNotification(result.message || 'Отчёт успешно сохранён');
    documentsUploadRef.value?.reset();
    window.dispatchEvent(new CustomEvent('residentReports:changed'));
    closeDrawer({ reset: true });
  } catch (error) {
    showStcErrorNotification(error instanceof Error ? error.message : 'Не удалось сохранить отчёт');
    documentsUploadRef.value?.resetSubmittingState();
  } finally {
    isSubmitting.value = false;
  }
}

defineExpose({ open: showDrawer, requestDelete: showDeleteConfirm });
</script>
